/**
 * 汎用の確認ダイアログ。
 *
 * - ブラウザネイティブの `window.confirm` だと、Chrome 等が
 *   「<ホスト名> の内容」というヘッダーを自動で付与する。これを表示しないために
 *   アプリ独自のモーダルとして実装する。
 * - 既存の `.modal-backdrop` / `.modal` / `.modal-header` / `.modal-body` /
 *   `.modal-footer` の CSS をそのまま流用してレイアウトを揃える。
 * - 背景クリック・ESC キーでは閉じない (誤操作防止)。OK / キャンセルボタンのみで
 *   操作させる。
 *
 * 改行は `\n` で受け取り、`<br />` に変換して複数行表示する。
 */
import { Fragment } from 'react';

export interface ConfirmDialogProps {
  /** 表示状態。false のときは何も描画しない。 */
  open: boolean;
  /** ヘッダーに表示するタイトル。省略時は「確認」。 */
  title?: string;
  /** 本文。`\n` で改行。 */
  message: string;
  /** 確定ボタンのラベル。省略時は「OK」。 */
  confirmLabel?: string;
  /** キャンセルボタンのラベル。省略時は「キャンセル」。 */
  cancelLabel?: string;
  /** 確定ボタンを赤系 (破壊的操作) スタイルにするか。既定 false。 */
  destructive?: boolean;
  /** 確定ボタンを無効化するか (処理中の二重送信防止用)。既定 false。 */
  confirmDisabled?: boolean;
  /** キャンセルボタンを無効化するか (処理中の操作禁止用)。既定 false。 */
  cancelDisabled?: boolean;
  /** 確定時のコールバック */
  onConfirm: () => void;
  /** キャンセル時のコールバック */
  onCancel: () => void;
}

export function ConfirmDialog(props: ConfirmDialogProps): React.JSX.Element | null {
  const {
    open,
    title = '確認',
    message,
    confirmLabel = 'OK',
    cancelLabel = 'キャンセル',
    destructive = false,
    confirmDisabled = false,
    cancelDisabled = false,
    onConfirm,
    onCancel,
  } = props;

  if (!open) return null;

  // 改行 (\n) を <br /> に変換して複数行表示
  const lines = message.split('\n');

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-testid="confirm-dialog"
      // 背景クリックでは閉じない (onClick 未設定)
      // window.confirm を踏襲して画面中央最上部 (上寄せ) に表示するため、
      // 親 .modal-backdrop の align-items: center を flex-start で上書きする。
      style={{ zIndex: 1300, alignItems: 'flex-start', paddingTop: '5vh' }}
    >
      <div className="modal modal--single-column" style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <h2 style={{ margin: 0, fontSize: 15 }}>{title}</h2>
        </div>
        <div className="modal-body modal-body--single-column">
          <div className="form-pane">
            <p style={{ margin: 0, whiteSpace: 'pre-wrap' }} data-testid="confirm-dialog-message">
              {lines.map((line, idx) => (
                <Fragment key={idx}>
                  {line}
                  {idx < lines.length - 1 ? <br /> : null}
                </Fragment>
              ))}
            </p>
          </div>
        </div>
        <div className="modal-footer">
          <button
            type="button"
            data-testid="confirm-dialog-cancel-button"
            onClick={onCancel}
            autoFocus={confirmDisabled && !cancelDisabled}
            disabled={cancelDisabled}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            data-testid="confirm-dialog-confirm-button"
            className={destructive ? 'danger' : 'primary'}
            onClick={onConfirm}
            disabled={confirmDisabled}
            autoFocus={!confirmDisabled && cancelDisabled}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
